export default function formatPercent(
  count: number,
  total: number,
  symbol = "%",
): string {
  const MAX_DECIMALS = 6;

  if (!total || total <= 0 || !count) return `0,0${symbol}`;

  const percent = (count / total) * 100;

  // Default 1 decimal
  const defaultDecimals = 1;
  const rounded = Number(percent.toFixed(defaultDecimals));
  if (rounded !== 0) {
    // If the rounded value is an integer (e.g. 1.0 -> 1), display as integer without decimals
    if (Number.isInteger(rounded)) {
      return `${rounded}${symbol}`;
    }
    // Format with 1 decimal, replace dot with comma
    return `${percent.toFixed(defaultDecimals).replace(".", ",")}${symbol}`;
  }

  // If rounded to 0 but actual percent > 0, increase precision up to MAX_DECIMALS
  for (let d = 2; d <= MAX_DECIMALS; d++) {
    const r = Number(percent.toFixed(d));
    if (r !== 0) {
      const s = percent.toFixed(d);
      let trimmed = s;
      if (s.includes(".")) {
        trimmed = s.replace(/(\.(\d+?))0+$/, "$1");
      }
      return `${trimmed.replace(".", ",")}${symbol}`;
    }
  }

  // If still zero (extremely small or rounding limitations), return with MAX_DECIMALS
  return `${percent.toFixed(MAX_DECIMALS).replace(".", ",")}${symbol}`;
}

/**
 * Formats two percentage values for display.
 * @param percentAll - The percentage based on all problems
 * @param percentSolved - The percentage based on solved problems only
 * @param hasSolvedProblems - Whether there are any solved problems
 * @returns Formatted string like "50% (80%)" or "0,0% (-)" or "100%"
 */
export function formatDualPercent(
  percentAll: number,
  percentSolved: number,
  hasSolvedProblems: boolean,
  symbol = "%",
): string {
  // Format the "all problems" percentage using existing formatPercent with empty symbol
  const formattedAll = formatPercent(percentAll, 100, "");

  // If no solved problems, show dash for the solved percentage
  if (!hasSolvedProblems) {
    return "-";
  }

  // If both percentages are equal (within 0.1 tolerance), show single value
  if (Math.abs(percentAll - percentSolved) < 0.1) {
    // Special case: preserve decimal formatting for zero
    if (percentAll === 0) {
      return `${formattedAll}${symbol}`;
    }
    // For near-equal non-zero values, show rounded integer percent
    return `${Math.round(percentAll)}${symbol}`;
  }

  // Format the "solved only" percentage
  const formattedSolved = formatPercent(percentSolved, 100, "");
  return `${formattedAll}${symbol} (${formattedSolved}${symbol})`;
}

export function formatRatingValue(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  // If rounded is integer (handles -0 -> "0" via String)
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  // Not an integer: format with one decimal and replace dot with comma
  return rounded.toFixed(1).replace(".", ",");
}

export function formatSignedRatingValue(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  const formattedValue = formatRatingValue(value);

  return rounded > 0 ? `+${formattedValue}` : formattedValue;
}
